import React, { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { api, endpoints } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as UserIcon } from '../../icons/user.svg';
import { ReactComponent as GroupIcon } from '../../icons/groups.svg';
import { ReactComponent as SettingsIcon } from '../../icons/settings.svg';
import { ReactComponent as SignatureIcon } from '../../icons/signatures.svg';
import { Card, Badge, Alert, Button, Input } from '../../components/ui';
import GraphConnectionCard from '../../components/setup/GraphConnectionCard';
import GoogleConnectionCard from '../../components/setup/GoogleConnectionCard';
import AddinDeploymentCard from '../../components/setup/AddinDeploymentCard';
import AddinBehaviorCard from '../../components/AddinBehaviorCard';
import GmailAddonDeploymentCard from '../../components/setup/GmailAddonDeploymentCard';
import { PERSONAL_PLACEHOLDERS, PERSONAL_PLACEHOLDER_LABELS } from '../../constants/signaturePlaceholders';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import { useT, pick, apiErrorMessage } from '../../i18n';
import './SettingsPage.css';

// Matches AddinDeploymentCard.jsx's own constant — this tab reads the live
// manifest directly (rather than hardcoding version/id here) specifically so
// it can never drift from whatever's actually deployed.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MANIFEST_URL = `${API_BASE_URL}/outlook-addin/manifest-validated.xml`;

const VALID_TABS = ['profile', 'organization', 'addin', 'placeholders'];

// The organization endpoint returns `license_status: {status, valid_until,
// days_remaining}` (organizations/serializers.py) and the plan on the
// organization itself — this page used to read a `license.is_active` /
// `license.plan_type` that never existed, so every organization always
// showed "Pasif / Belirtilmemiş".
const LICENSE_STATUS_VARIANTS = {
  active: 'success', trial: 'info', pending: 'warning', expired: 'danger', suspended: 'danger', cancelled: 'danger',
};

const messages = {
  licenseStatus: {
    tr: { active: 'Aktif', trial: 'Deneme', pending: 'Ödeme Bekliyor', expired: 'Süresi Dolmuş', suspended: 'Askıya Alınmış', cancelled: 'İptal Edilmiş' },
    en: { active: 'Active', trial: 'Trial', pending: 'Awaiting Payment', expired: 'Expired', suspended: 'Suspended', cancelled: 'Cancelled' },
  },
  plans: {
    tr: { free: 'Ücretsiz', starter: 'Başlangıç', business: 'İş', enterprise: 'Kurumsal', pro: 'Pro' },
    en: { free: 'Free', starter: 'Starter', business: 'Business', enterprise: 'Enterprise', pro: 'Pro' },
  },
  docTitle: { tr: 'Ayarlar - Signature Manager', en: 'Settings - Signature Manager' },
  orgLoadFailed: { tr: 'Organizasyon ayarları yüklenemedi', en: 'Couldn’t load organization settings' },
  manifestParse: { tr: 'Manifest ayrıştırılamadı', en: 'Couldn’t parse the manifest' },
  addinLoadFailed: { tr: 'Eklenti bilgileri yüklenemedi', en: 'Couldn’t load add-in details' },
  otherProvider: {
    tr: 'Zaten bir dizin sağlayıcısına bağlısınız. Değiştirmek için önce mevcut bağlantıyı kaldırın.',
    en: 'You’re already connected to a directory provider. Remove the current connection first to switch.',
  },
  placeholderLoadFailed: { tr: 'Placeholder değerleri yüklenemedi', en: 'Couldn’t load placeholder values' },
  placeholderSaved: { tr: 'Placeholder değerleri kaydedildi!', en: 'Placeholder values saved!' },
  placeholderSaveFailed: { tr: 'Placeholder değerleri kaydedilemedi', en: 'Couldn’t save placeholder values' },
  inviteNoEmail: { tr: 'Davet oluşturuldu fakat e-posta gönderilemedi. Lütfen daha sonra tekrar deneyin.', en: 'The invitation was created but the email couldn’t be sent. Please try again later.' },
  inviteFailed: { tr: 'Davet gönderilemedi', en: 'Couldn’t send the invitation' },
  roleFailed: { tr: 'Rol değiştirilemedi', en: 'Couldn’t change the role' },
  roleSuperAdmin: { tr: 'Süper Admin', en: 'Super Admin' },
  roleOrgAdmin: { tr: 'Organizasyon Admin', en: 'Organization Admin' },
  roleUser: { tr: 'Kullanıcı', en: 'User' },
  title: { tr: 'Ayarlar', en: 'Settings' },
  subtitle: { tr: 'Profil ve organizasyon ayarlarınızı yönetin', en: 'Manage your profile and organization settings' },
  tabProfile: { tr: 'Profil', en: 'Profile' },
  tabOrganization: { tr: 'Organizasyon', en: 'Organization' },
  tabAddin: { tr: 'Eklenti Bilgileri', en: 'Add-in Details' },
  tabAddinSettings: { tr: 'Eklenti Ayarları', en: 'Add-in Settings' },
  profileInfo: { tr: 'Profil Bilgileri', en: 'Profile Details' },
  fullName: { tr: 'Ad Soyad', en: 'Full Name' },
  jobTitle: { tr: 'Unvan', en: 'Job Title' },
  department: { tr: 'Ofis / Departman', en: 'Office / Department' },
  phone: { tr: 'Telefon', en: 'Phone' },
  mobile: { tr: 'Mobil', en: 'Mobile' },
  address: { tr: 'Adres', en: 'Address' },
  profileHint: {
    tr: 'Bu bilgiler Microsoft 365 hesabınızdan alınır ve her girişinizde güncellenir; portaldan değiştirilemez. Değiştirmek için Microsoft 365 profilinizi (veya BT yöneticinizi) kullanın.',
    en: 'These details come from your Microsoft 365 account and refresh every time you sign in; they can’t be changed in the portal. To change them, use your Microsoft 365 profile (or ask your IT administrator).',
  },
  interfaceLanguage: { tr: 'Arayüz Dili', en: 'Interface Language' },
  interfaceLanguageHint: {
    tr: 'Portalın görüntülendiği dil. Seçiminiz bu tarayıcıda hatırlanır. İmzalarınızın dili bundan bağımsızdır, şablonlardan belirlenir.',
    en: 'The language the portal is shown in. Your choice is remembered in this browser. Your signatures’ language is separate and set by the templates.',
  },
  accountInfo: { tr: 'Hesap Bilgileri', en: 'Account Details' },
  organization: { tr: 'Organizasyon', en: 'Organization' },
  notSpecified: { tr: 'Belirtilmemiş', en: 'Not specified' },
  role: { tr: 'Rol', en: 'Role' },
  accountStatus: { tr: 'Hesap Durumu', en: 'Account Status' },
  joined: { tr: 'Kayıt Tarihi', en: 'Joined' },
  orgInfo: { tr: 'Organizasyon Bilgileri', en: 'Organization Details' },
  orgName: { tr: 'Organizasyon Adı', en: 'Organization Name' },
  customerNumber: { tr: 'Müşteri Numarası', en: 'Customer Number' },
  orgId: { tr: 'Organizasyon ID', en: 'Organization ID' },
  licenseStatusLabel: { tr: 'Lisans Durumu', en: 'License Status' },
  noLicense: { tr: 'Lisans yok', en: 'No license' },
  licenseType: { tr: 'Lisans Tipi', en: 'License Type' },
  licenseEnd: { tr: 'Lisans Bitiş Tarihi', en: 'License End Date' },
  daysLeft: { tr: ' ({n} gün kaldı)', en: ({ n }) => ` (${n} day${n === 1 ? '' : 's'} left)` },
  trialPending: {
    tr: '14 günlük deneme, ilk imzanız bir e-postaya eklendiğinde başlar.',
    en: 'Your 14-day trial starts when your first signature is added to an email.',
  },
  setupWindow: { tr: 'Kurulum için son tarih: {date}', en: 'Complete setup by: {date}' },
  createdAt: { tr: 'Oluşturma Tarihi', en: 'Created' },
  orgHint: {
    tr: 'Organizasyon bilgileri kayıt sırasında belirlenir ve buradan değiştirilemez. Değişiklik için destek ekibimizle iletişime geçin.',
    en: 'Organization details are set at registration and can’t be changed here. Contact our support team to change them.',
  },
  domains: { tr: 'Etki Alanları', en: 'Domains' },
  domainsHint: {
    tr: () => <>Bu etki alanlarından birine ait e-posta adresiyle Microsoft ile giriş yapan herkes otomatik olarak organizasyonunuza <strong>Kullanıcı</strong> rolüyle katılır. Etki alanları Microsoft 365 bağlantınızdan otomatik alınır; eklemek veya çıkarmak için destek ekibimizle iletişime geçin.</>,
    en: () => <>Anyone who signs in with Microsoft using an email address on one of these domains joins your organization automatically with the <strong>User</strong> role. Domains are taken from your Microsoft 365 connection automatically; contact our support team to add or remove one.</>,
  },
  domain: { tr: 'Etki Alanı', en: 'Domain' },
  source: { tr: 'Kaynak', en: 'Source' },
  verification: { tr: 'Doğrulama', en: 'Verification' },
  noDomains: { tr: 'Henüz tanımlı etki alanı yok. Microsoft 365 bağlantısını kurduğunuzda otomatik eklenir.', en: 'No domains yet. They’re added automatically when you connect Microsoft 365.' },
  primary: { tr: 'Birincil', en: 'Primary' },
  manual: { tr: 'Manuel', en: 'Manual' },
  verified: { tr: 'Doğrulandı', en: 'Verified' },
  notVerified: { tr: 'Doğrulanmadı', en: 'Not verified' },
  teamMembers: { tr: 'Ekip Üyeleri', en: 'Team Members' },
  sendInvite: { tr: 'Davet Gönder', en: 'Send Invitation' },
  teamHint: {
    tr: 'Etki alanlarınızdan Microsoft ile giriş yapan kişiler bu listeye kendiliğinden eklenir, ayrıca davet göndermeniz gerekmez. Yönetici yetkisi vermek için aşağıdan rolünü değiştirebilirsiniz. Davet, etki alanlarınız dışındaki kişiler (örn. dış danışman) için kullanılır.',
    en: 'People who sign in with Microsoft from your domains are added to this list automatically — no invitation needed. To give someone admin rights, change their role below. Invitations are for people outside your domains (e.g. an external consultant).',
  },
  noMembers: { tr: 'Ekip üyesi bulunamadı.', en: 'No team members found.' },
  deactivate: { tr: 'Pasifleştir', en: 'Deactivate' },
  activate: { tr: 'Aktifleştir', en: 'Activate' },
  pendingInvites: { tr: 'Bekleyen Davetler', en: 'Pending Invitations' },
  expires: { tr: 'Son Geçerlilik', en: 'Expires' },
  resend: { tr: 'Yeniden Gönder', en: 'Resend' },
  revoke: { tr: 'İptal Et', en: 'Revoke' },
  inviteTitle: { tr: 'Ekibe Davet Gönder', en: 'Invite to the Team' },
  inviteHint: {
    tr: 'Etki alanlarınızdan birindeki (bkz. Etki Alanları) kişiler zaten Microsoft ile girerek katılabilir; davet en çok dış kişiler veya baştan yönetici yetkisi vermek için gereklidir.',
    en: 'People on one of your domains (see Domains) can already join by signing in with Microsoft; invitations are mainly for outside people or for granting admin rights from the start.',
  },
  emailRequired: { tr: 'E-posta *', en: 'Email *' },
  emailPlaceholder: { tr: 'ornek@sirket.com', en: 'name@company.com' },
  roleRequired: { tr: 'Rol *', en: 'Role *' },
  sending: { tr: 'Gönderiliyor...', en: 'Sending...' },
  appName: { tr: 'Uygulama Adı', en: 'App Name' },
  provider: { tr: 'Sağlayıcı', en: 'Provider' },
  version: { tr: 'Versiyon', en: 'Version' },
  appId: { tr: 'Uygulama ID', en: 'App ID' },
  lastUpdated: { tr: 'Son Güncelleme', en: 'Last Updated' },
  manifestHint: {
    tr: 'Aşağıdaki adres, Microsoft 365 Admin Center\'da eklentiyi kurarken veya güncellerken (Tümleşik Uygulamalar > Manifest dosyası sağla > URL\'den yükle) kullanılır.',
    en: 'Use the address below when installing or updating the add-in in the Microsoft 365 admin center (Integrated apps > Upload custom apps > Provide link to manifest file).',
  },
  copied: { tr: 'Kopyalandı ✓', en: 'Copied ✓' },
  copy: { tr: 'Kopyala', en: 'Copy' },
  placeholderHint: {
    tr: 'İmza şablonu düzenlerken Canlı Önizleme\'de gösterilen örnek değerler. Sadece önizlemeyi etkiler — gerçek e-postada bu değerler alıcının kendi bilgileriyle değişir. Doldurulmamış alanlar, Microsoft/Google hesabınızla giriş yaptığınız bilgilerle önceden doldurulur.',
    en: 'Sample values shown in the Live Preview while editing a signature template. They only affect the preview — in a real email they’re replaced with each sender’s own details. Empty fields are prefilled with the details from the Microsoft/Google account you signed in with.',
  },
};

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { t, lang, formatDate } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);
  const location = useLocation();
  // Lets other pages deep-link into a specific tab (e.g. SignatureForm.jsx's
  // "buraya tıkla" link into Settings > Placeholder) via ?tab=placeholders,
  // instead of always landing on Profil and making the admin click again.
  const initialTab = new URLSearchParams(location.search).get('tab');
  // Admins land on the add-in tab (its switches are what they come here
  // for most); everyone else only has Profil.
  const defaultTab = ['org_admin', 'super_admin'].includes(user?.role) ? 'addin' : 'profile';
  const [activeTab, setActiveTab] = useState(
    VALID_TABS.includes(initialTab) ? initialTab : defaultTab
  );
  const [orgLoading, setOrgLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organization, setOrganization] = useState(null);

  // Profile details (job title, phones, office) as Microsoft 365 has them —
  // best-effort; the account record's own name/email show regardless.
  const [profileDirectory, setProfileDirectory] = useState(null);
  useEffect(() => {
    if (activeTab !== 'profile') return;
    let cancelled = false;
    api.get(endpoints.auth.meDirectory)
      .then((data) => { if (!cancelled) setProfileDirectory(data || {}); })
      .catch(() => { if (!cancelled) setProfileDirectory({}); });
    return () => { cancelled = true; };
  }, [activeTab]);

  const fetchOrganization = React.useCallback(async () => {
    try {
      setOrgLoading(true);
      setError(null);

      if (user?.organization) {
        const response = await api.get(
          endpoints.admin.organizations.detail(user.organization)  // user.organization is the ID
        );
        setOrganization(response);  // response is already the data
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError(apiErrorMessage(err, messages.orgLoadFailed));
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setOrgLoading(false);
    }
  }, [user?.organization, logout]);

  useEffect(() => {
    if (activeTab === 'organization' && user?.organization) {
      fetchOrganization();
    }
  }, [activeTab, user?.organization, fetchOrganization]);

  // Drives whether AddinDeploymentCard still needs to be shown here — once
  // the admin has marked the add-in as deployed, the guide is no longer
  // useful clutter (GraphConnectionCard stays regardless, it's also how you
  // disconnect Microsoft 365).
  const [msStatus, setMsStatus] = useState(null);
  const fetchMsStatus = React.useCallback(async () => {
    try {
      setMsStatus(await api.get(endpoints.organizations.graphStatus));
    } catch {
      setMsStatus(null);
    }
  }, []);
  const [googleStatus, setGoogleStatus] = useState(null);
  const fetchGoogleStatus = React.useCallback(async () => {
    try {
      setGoogleStatus(await api.get(endpoints.organizations.googleStatus));
    } catch {
      setGoogleStatus(null);
    }
  }, []);
  // Domains that route Microsoft SSO logins to this organization (see
  // organizations/sso_login.py) — read-only here, managed by the platform.
  const [orgDomains, setOrgDomains] = useState(null);
  const fetchOrgDomains = React.useCallback(async () => {
    try {
      setOrgDomains(await api.get(endpoints.organizations.domains));
    } catch {
      setOrgDomains([]);
    }
  }, []);
  useEffect(() => {
    if (activeTab === 'organization' && user?.organization) {
      fetchMsStatus();
      fetchGoogleStatus();
      fetchOrgDomains();
    }
  }, [activeTab, user?.organization, fetchMsStatus, fetchGoogleStatus, fetchOrgDomains]);

  // Eklenti Bilgileri tab — reads Id/Version/ProviderName/DisplayName straight
  // out of the live manifest XML (rather than hardcoding them here) so this
  // can never go stale after a manifest deploy; Last-Modified comes from the
  // HTTP response itself since the manifest has no internal timestamp field.
  const [addinManifestInfo, setAddinManifestInfo] = useState(null);
  const [addinManifestLoading, setAddinManifestLoading] = useState(false);
  const [addinManifestError, setAddinManifestError] = useState(null);
  const [manifestCopied, setManifestCopied] = useState(false);

  const fetchAddinManifestInfo = React.useCallback(async () => {
    try {
      setAddinManifestLoading(true);
      setAddinManifestError(null);
      const response = await fetch(MANIFEST_URL);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const xmlText = await response.text();
      const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
      if (doc.querySelector('parsererror')) throw new Error(pick(messages.manifestParse));

      setAddinManifestInfo({
        id: doc.querySelector('Id')?.textContent || '',
        version: doc.querySelector('Version')?.textContent || '',
        providerName: doc.querySelector('ProviderName')?.textContent || '',
        displayName: doc.querySelector('DisplayName')?.getAttribute('DefaultValue') || '',
        lastModified: response.headers.get('last-modified') || null,
      });
    } catch (err) {
      console.error('Error fetching add-in manifest info:', err);
      setAddinManifestError(pick(messages.addinLoadFailed));
    } finally {
      setAddinManifestLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'addin') {
      fetchAddinManifestInfo();
    }
  }, [activeTab, fetchAddinManifestInfo]);
  const otherProviderHint = t('otherProvider');

  // Placeholder tab — admin-editable sample values for SignatureForm.jsx's
  // live preview. Unsaved fields default to the logged-in admin's own
  // directory profile (graph_defaults from the backend), so the form reads
  // as "here's what it looks like with your info" on first open rather than
  // starting blank.
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [placeholderLoading, setPlaceholderLoading] = useState(false);
  const [placeholderSaving, setPlaceholderSaving] = useState(false);
  const [placeholderError, setPlaceholderError] = useState(null);
  const [placeholderSuccess, setPlaceholderSuccess] = useState(null);

  const fetchPlaceholders = React.useCallback(async () => {
    try {
      setPlaceholderLoading(true);
      setPlaceholderError(null);
      const data = await api.get(endpoints.signatures.previewPlaceholders);
      const merged = {};
      PERSONAL_PLACEHOLDERS.forEach(token => {
        merged[token] = data.overrides?.[token] || data.graph_defaults?.[token] || '';
      });
      setPlaceholderValues(merged);
    } catch (err) {
      console.error('Error fetching preview placeholders:', err);
      setPlaceholderError(pick(messages.placeholderLoadFailed));
    } finally {
      setPlaceholderLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'placeholders') {
      fetchPlaceholders();
    }
  }, [activeTab, fetchPlaceholders]);

  const handlePlaceholderChange = (token, value) => {
    setPlaceholderValues(prev => ({ ...prev, [token]: value }));
  };

  const handlePlaceholderSave = async (e) => {
    e.preventDefault();
    try {
      setPlaceholderSaving(true);
      setPlaceholderError(null);
      setPlaceholderSuccess(null);
      await api.put(endpoints.signatures.previewPlaceholders, { values: placeholderValues });
      setPlaceholderSuccess(t('placeholderSaved'));
      setTimeout(() => setPlaceholderSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving preview placeholders:', err);
      setPlaceholderError(apiErrorMessage(err, messages.placeholderSaveFailed));
    } finally {
      setPlaceholderSaving(false);
    }
  };

  // Team members (local login accounts, scoped server-side to the caller's
  // own org) + pending invites — the self-service replacement for "only a
  // super admin can add a teammate through the admin panel".
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'org_user' });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  const fetchTeamMembers = React.useCallback(async () => {
    try {
      setTeamMembersLoading(true);
      const response = await api.get(endpoints.admin.users.list);
      // /api/admin/users/ is a ModelViewSet — paginated (DRF's default
      // PageNumberPagination wraps it as {results: [...]}, not a bare array).
      setTeamMembers(response.results || response || []);
    } catch (err) {
      console.error('Error fetching team members:', err);
    } finally {
      setTeamMembersLoading(false);
    }
  }, []);

  const fetchPendingInvites = React.useCallback(async () => {
    try {
      setInvitesLoading(true);
      const response = await api.get(endpoints.invites.list, { params: { status: 'pending' } });
      setPendingInvites(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching pending invites:', err);
    } finally {
      setInvitesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'organization' && user?.organization) {
      fetchTeamMembers();
      fetchPendingInvites();
    }
  }, [activeTab, user?.organization, fetchTeamMembers, fetchPendingInvites]);

  const handleInviteFormChange = (e) => {
    const { name, value } = e.target;
    setInviteForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    try {
      setInviteSubmitting(true);
      setInviteError(null);
      const response = await api.post(endpoints.invites.create, inviteForm);
      if (response.email_sent === false) {
        setInviteError(t('inviteNoEmail'));
      } else {
        setShowInviteModal(false);
        setInviteForm({ email: '', role: 'org_user' });
      }
      await fetchPendingInvites();
    } catch (err) {
      setInviteError(apiErrorMessage(err, messages.inviteFailed));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleRevokeInvite = async (id) => {
    try {
      await api.post(endpoints.invites.revoke(id));
      await fetchPendingInvites();
    } catch (err) {
      console.error('Error revoking invite:', err);
    }
  };

  const handleResendInvite = async (id) => {
    try {
      await api.post(endpoints.invites.resend(id));
      await fetchPendingInvites();
    } catch (err) {
      console.error('Error resending invite:', err);
    }
  };

  const handleToggleMemberActive = async (member) => {
    try {
      const action = member.is_active ? endpoints.admin.users.deactivate : endpoints.admin.users.activate;
      await api.post(action(member.id));
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error toggling member active state:', err);
    }
  };

  const handleChangeMemberRole = async (member, newRole) => {
    if (newRole === member.role) return;
    try {
      await api.post(endpoints.admin.users.changeRole(member.id), { role: newRole });
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error changing member role:', err);
      alert(apiErrorMessage(err, messages.roleFailed));
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return t('roleSuperAdmin');
      case 'org_admin': return t('roleOrgAdmin');
      default: return t('roleUser');
    }
  };

  const roleBadgeVariant = (role) => {
    if (role === 'super_admin') return 'purple';
    if (role === 'org_admin') return 'info';
    return 'success';
  };

  const isOrgAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';

  // Marka Varlıkları moved to its own top-level page (/brand-assets) — old
  // ?tab=brandAssets deep-links (bookmarks, anything still pointing at the
  // removed tab) land here instead of silently falling back to Profil.
  if (initialTab === 'brandAssets') {
    return <Navigate to="/brand-assets" replace />;
  }

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="settings-header">
        <div className="settings-title-section">
          <SettingsIcon className="settings-header-icon" />
          <div>
            <h1>{t('title')}</h1>
            <p>{t('subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        {isOrgAdmin && (
          <button
            className={`settings-tab ${activeTab === 'addin' ? 'active' : ''}`}
            onClick={() => setActiveTab('addin')}
          >
            <SignatureIcon className="tab-icon" />
            <span>{t('tabAddinSettings')}</span>
          </button>
        )}
        <button
          className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <UserIcon className="tab-icon" />
          <span>{t('tabProfile')}</span>
        </button>
        {isOrgAdmin && (
          <button
            className={`settings-tab ${activeTab === 'organization' ? 'active' : ''}`}
            onClick={() => setActiveTab('organization')}
          >
            <GroupIcon className="tab-icon" />
            <span>{t('tabOrganization')}</span>
          </button>
        )}
        {isOrgAdmin && (
          <button
            className={`settings-tab ${activeTab === 'placeholders' ? 'active' : ''}`}
            onClick={() => setActiveTab('placeholders')}
          >
            <UserIcon className="tab-icon" />
            <span>Placeholder</span>
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </Alert>
      )}

      {/* Profile Tab Content */}
      {activeTab === 'profile' && (
        <>
          {/* Profile Information Card — read-only: name/email come from the
              user's Microsoft account (refreshed at every sign-in,
              organizations/sso_login.py), the rest from the org's directory. */}
          <Card className="settings-card">
            <div className="settings-card-header">
              <SettingsIcon className="section-icon" />
              <h2>{t('profileInfo')}</h2>
            </div>

            <div className="account-info-grid">
              <div className="info-item">
                <div className="info-label">{t('fullName')}</div>
                <div className="info-value">
                  {`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || profileDirectory?.display_name || '—'}
                </div>
              </div>

              <div className="info-item">
                <div className="info-label">{t('email')}</div>
                <div className="info-value">{user?.email || '—'}</div>
              </div>

              {[
                ['job_title', t('jobTitle')],
                ['department_or_location', t('department')],
                ['phone', t('phone')],
                ['mobile', t('mobile')],
                ['office', t('address')],
              ].filter(([key]) => profileDirectory?.[key]).map(([key, label]) => (
                <div className="info-item" key={key}>
                  <div className="info-label">{label}</div>
                  <div className="info-value">{profileDirectory[key]}</div>
                </div>
              ))}
            </div>

            <p className="field-hint-standalone">{t('profileHint')}</p>
          </Card>

          <Card className="settings-card">
            <div className="settings-card-header">
              <SettingsIcon className="section-icon" />
              <h2>{t('interfaceLanguage')}</h2>
            </div>
            <LanguageSwitcher tone="light" />
            <p className="field-hint-standalone">{t('interfaceLanguageHint')}</p>
          </Card>

          {/* Account Information Card */}
          <Card className="settings-card">
            <div className="settings-card-header">
              <UserIcon className="section-icon" />
              <h2>{t('accountInfo')}</h2>
            </div>

            <div className="account-info-grid">
              <div className="info-item">
                <div className="info-label">{t('organization')}</div>
                <div className="info-value">{user?.organization_name || t('notSpecified')}</div>
              </div>

              <div className="info-item">
                <div className="info-label">{t('role')}</div>
                <div className="info-value">
                  <Badge variant={roleBadgeVariant(user?.role)}>{getRoleLabel(user?.role)}</Badge>
                </div>
              </div>

              <div className="info-item">
                <div className="info-label">{t('accountStatus')}</div>
                <div className="info-value">
                  <Badge variant={user?.is_active ? 'success' : 'danger'}>
                    {user?.is_active ? t('active') : t('inactive')}
                  </Badge>
                </div>
              </div>

              <div className="info-item">
                <div className="info-label">{t('joined')}</div>
                <div className="info-value">
                  {user?.date_joined ? formatDate(user.date_joined) : t('notSpecified')}
                </div>
              </div>
            </div>
          </Card>

        </>
      )}

      {/* Organization Tab Content */}
      {activeTab === 'organization' && isOrgAdmin && (
        <>
          {orgLoading ? (
            <Card className="settings-card">
              <p className="settings-inline-loading">{t('loading')}</p>
            </Card>
          ) : (
            <>
              {/* Organization Information Card */}
              <Card className="settings-card">
                <div className="settings-card-header">
                  <GroupIcon className="section-icon" />
                  <h2>{t('orgInfo')}</h2>
                </div>

                <div className="account-info-grid">
                    <div className="info-item">
                      <div className="info-label">{t('orgName')}</div>
                      <div className="info-value">{organization?.name || 'N/A'}</div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">{t('customerNumber')}</div>
                      <div className="info-value">{organization?.customer_number || 'N/A'}</div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">{t('orgId')}</div>
                      <div className="info-value">{organization?.id || 'N/A'}</div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">{t('licenseStatusLabel')}</div>
                      <div className="info-value">
                        {organization?.license_status ? (
                          <Badge variant={LICENSE_STATUS_VARIANTS[organization.license_status.status] || 'warning'}>
                            {t('licenseStatus')[organization.license_status.status] || organization.license_status.status}
                          </Badge>
                        ) : (
                          <Badge variant="danger">{t('noLicense')}</Badge>
                        )}
                      </div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">{t('licenseType')}</div>
                      <div className="info-value">
                        <Badge variant="info">{t('plans')[organization?.plan] || organization?.plan || t('notSpecified')}</Badge>
                      </div>
                    </div>

                    {organization?.license_status?.trial_pending ? (
                      <div className="info-item">
                        <div className="info-label">{t('licenseEnd')}</div>
                        <div className="info-value">
                          {t('trialPending')}
                          <div className="info-hint">{t('setupWindow', { date: formatDate(organization.license_status.valid_until) })}</div>
                        </div>
                      </div>
                    ) : organization?.license_status?.valid_until && (
                      <div className="info-item">
                        <div className="info-label">{t('licenseEnd')}</div>
                        <div className="info-value">
                          {formatDate(organization.license_status.valid_until)}
                          {typeof organization.license_status.days_remaining === 'number' && organization.license_status.days_remaining >= 0 &&
                            t('daysLeft', { n: organization.license_status.days_remaining })}
                        </div>
                      </div>
                    )}

                    <div className="info-item">
                      <div className="info-label">{t('createdAt')}</div>
                      <div className="info-value">
                        {organization?.created_at
                          ? formatDate(organization.created_at)
                          : t('notSpecified')}
                      </div>
                    </div>
                </div>
                <p className="field-hint-standalone">{t('orgHint')}</p>
              </Card>

              {/* Domains Card — the email domains that route Microsoft SSO
                  logins to this organization (organizations/sso_login.py). */}
              <Card className="settings-card">
                <div className="settings-card-header">
                  <GroupIcon className="section-icon" />
                  <h2>{t('domains')}</h2>
                </div>
                <p className="field-hint-standalone">{t('domainsHint')}</p>
                {orgDomains === null ? (
                  <p className="settings-inline-loading">{t('loading')}</p>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('domain')}</th>
                        <th>{t('source')}</th>
                        <th>{t('verification')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgDomains.length === 0 && (
                        <tr>
                          <td colSpan={3} className="data-table-empty">
                            {t('noDomains')}
                          </td>
                        </tr>
                      )}
                      {orgDomains.map(d => (
                        <tr key={d.domain}>
                          <td>
                            {d.domain}{' '}
                            {d.is_primary && <Badge variant="info">{t('primary')}</Badge>}
                          </td>
                          <td>{d.source === 'graph_sync' ? 'Microsoft 365' : t('manual')}</td>
                          <td>
                            {d.verified_at ? (
                              <Badge variant="success">{t('verified')}</Badge>
                            ) : (
                              <Badge variant="warning">{t('notVerified')}</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>

              {/* Team Members Card */}
              <Card className="settings-card">
                <div className="settings-card-header team-card-header">
                  <div className="team-card-header-title">
                    <UserIcon className="section-icon" />
                    <h2>{t('teamMembers')}</h2>
                  </div>
                  <Button type="button" onClick={() => { setInviteError(null); setShowInviteModal(true); }}>
                    {t('sendInvite')}
                  </Button>
                </div>
                <p className="field-hint-standalone">{t('teamHint')}</p>

                {teamMembersLoading ? (
                  <p className="settings-inline-loading">{t('loading')}</p>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('fullName')}</th>
                        <th>{t('email')}</th>
                        <th>{t('role')}</th>
                        <th>{t('status')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamMembers.length === 0 && (
                        <tr><td colSpan={5} className="data-table-empty">{t('noMembers')}</td></tr>
                      )}
                      {teamMembers.map(member => (
                        <tr key={member.id}>
                          <td>{`${member.first_name || ''} ${member.last_name || ''}`.trim() || '—'}</td>
                          <td>{member.email}</td>
                          <td>
                            {member.id === user.id ? (
                              <Badge variant={roleBadgeVariant(member.role)}>{getRoleLabel(member.role)}</Badge>
                            ) : (
                              <select
                                className="team-role-select"
                                value={member.role}
                                onChange={(e) => handleChangeMemberRole(member, e.target.value)}
                                disabled={member.role === 'super_admin' && user.role !== 'super_admin'}
                              >
                                <option value="org_user">{t('roleUser')}</option>
                                <option value="org_admin">{t('roleOrgAdmin')}</option>
                                {user.role === 'super_admin' && <option value="super_admin">{t('roleSuperAdmin')}</option>}
                              </select>
                            )}
                          </td>
                          <td>
                            <Badge variant={member.is_active ? 'success' : 'danger'}>
                              {member.is_active ? t('active') : t('inactive')}
                            </Badge>
                          </td>
                          <td>
                            {member.id !== user.id && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => handleToggleMemberActive(member)}
                              >
                                {member.is_active ? t('deactivate') : t('activate')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {(invitesLoading || pendingInvites.length > 0) && (
                  <>
                    <h3 className="team-subsection-heading">{t('pendingInvites')}</h3>
                    {invitesLoading ? (
                      <p className="settings-inline-loading">{t('loading')}</p>
                    ) : (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>{t('email')}</th>
                            <th>{t('role')}</th>
                            <th>{t('expires')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingInvites.map(invite => (
                            <tr key={invite.id}>
                              <td>{invite.email}</td>
                              <td><Badge variant="info">{invite.role ? getRoleLabel(invite.role) : invite.role_display}</Badge></td>
                              <td>{formatDate(invite.expires_at)}</td>
                              <td className="team-invite-actions">
                                <Button type="button" variant="secondary" size="sm" onClick={() => handleResendInvite(invite.id)}>
                                  {t('resend')}
                                </Button>
                                <Button type="button" variant="secondary" size="sm" onClick={() => handleRevokeInvite(invite.id)}>
                                  {t('revoke')}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </Card>

              {showInviteModal && (
                <div className="invite-modal-overlay" onClick={() => setShowInviteModal(false)}>
                  <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="invite-modal-header">
                      <h2>{t('inviteTitle')}</h2>
                      <button type="button" className="invite-modal-close-btn" onClick={() => setShowInviteModal(false)}>×</button>
                    </div>
                    <form onSubmit={handleSendInvite}>
                      {inviteError && <Alert variant="error">{inviteError}</Alert>}
                      <p className="field-hint-standalone">{t('inviteHint')}</p>
                      <Input
                        label={t('emailRequired')}
                        type="email"
                        name="email"
                        value={inviteForm.email}
                        onChange={handleInviteFormChange}
                        placeholder={t('emailPlaceholder')}
                        required
                      />
                      <div className="ui-form-group">
                        <label>{t('roleRequired')}</label>
                        <select name="role" value={inviteForm.role} onChange={handleInviteFormChange}>
                          <option value="org_user">{t('roleUser')}</option>
                          <option value="org_admin">{t('roleOrgAdmin')}</option>
                        </select>
                      </div>
                      <div className="invite-modal-footer">
                        <Button type="button" variant="secondary" onClick={() => setShowInviteModal(false)}>
                          {t('cancel')}
                        </Button>
                        <Button type="submit" disabled={inviteSubmitting}>
                          {inviteSubmitting ? t('sending') : t('sendInvite')}
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              <GraphConnectionCard disabledReason={googleStatus?.connected ? otherProviderHint : null} />
              {msStatus?.connected && !msStatus?.addin_deployed && <AddinDeploymentCard onDeployed={fetchMsStatus} />}
              {/* Microsoft 365 only at launch; kept for orgs already on Google. */}
              {googleStatus?.connected && <GoogleConnectionCard />}
              {googleStatus?.connected && !googleStatus?.addon_deployed && <GmailAddonDeploymentCard />}
            </>
          )}
        </>
      )}

      {/* Eklenti Ayarları Tab Content — the switches first, manifest details below */}
      {activeTab === 'addin' && isOrgAdmin && <AddinBehaviorCard />}
      {activeTab === 'addin' && isOrgAdmin && (
        <Card className="settings-card">
          <div className="settings-card-header">
            <SignatureIcon className="section-icon" />
            <h2>{t('tabAddin')}</h2>
          </div>

          {addinManifestLoading ? (
            <p className="settings-inline-loading">{t('loading')}</p>
          ) : addinManifestError ? (
            <Alert variant="error">{addinManifestError}</Alert>
          ) : addinManifestInfo && (
            <>
              <div className="account-info-grid">
                <div className="info-item">
                  <div className="info-label">{t('appName')}</div>
                  <div className="info-value">{addinManifestInfo.displayName || t('notSpecified')}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('provider')}</div>
                  <div className="info-value">{addinManifestInfo.providerName || t('notSpecified')}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('version')}</div>
                  <div className="info-value"><Badge variant="info">{addinManifestInfo.version || t('notSpecified')}</Badge></div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('appId')}</div>
                  <div className="info-value">{addinManifestInfo.id || t('notSpecified')}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('lastUpdated')}</div>
                  <div className="info-value">
                    {addinManifestInfo.lastModified
                      ? formatDate(addinManifestInfo.lastModified, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : t('notSpecified')}
                  </div>
                </div>
              </div>

              <p className="field-hint-standalone">{t('manifestHint')}</p>
              <div className="addin-deployment-manifest-box">
                <code>{MANIFEST_URL}</code>
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(MANIFEST_URL);
                      setManifestCopied(true);
                      setTimeout(() => setManifestCopied(false), 2000);
                    } catch {
                      // Clipboard API unavailable — the URL is still selectable/copyable by hand.
                    }
                  }}
                >
                  {manifestCopied ? t('copied') : t('copy')}
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      {activeTab === 'placeholders' && isOrgAdmin && (
        <Card className="settings-card">
          <div className="settings-card-header">
            <UserIcon className="section-icon" />
            <h2>Placeholder</h2>
          </div>
          <p className="field-hint-standalone">{t('placeholderHint')}</p>

          {placeholderLoading ? (
            <p className="settings-inline-loading">{t('loading')}</p>
          ) : (
            <form onSubmit={handlePlaceholderSave}>
              <div className="placeholder-input-grid">
                {PERSONAL_PLACEHOLDERS.map(token => (
                  <Input
                    key={token}
                    label={PERSONAL_PLACEHOLDER_LABELS[token] ? pick(PERSONAL_PLACEHOLDER_LABELS[token], undefined, lang) : token}
                    type="text"
                    value={placeholderValues[token] || ''}
                    onChange={e => handlePlaceholderChange(token, e.target.value)}
                  />
                ))}
              </div>

              {placeholderError && <Alert variant="error">{placeholderError}</Alert>}
              {placeholderSuccess && <Alert variant="success">{placeholderSuccess}</Alert>}

              <Button type="submit" disabled={placeholderSaving}>
                {placeholderSaving ? t('saving') : t('save')}
              </Button>
            </form>
          )}
        </Card>
      )}

    </div>
  );
};

export default SettingsPage;
